import { X } from 'lucide-react';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNivelesQuery } from '../../services/hooks/useNiveles';
import { fetchEventosByDateRangeAndNivel, useVerificarDisponibilidadPlazas, useDisponibilidadNivelQuery, fetchDisponibilidadNivel } from '../../services';
import { toast } from 'react-toastify';
import { Evento, EventoConDia } from '../../types/index';
import ConfiguracionNiveles from './ConfiguracionNiveles';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfigurationsQuery } from '../../services/hooks';

interface ModalProps {
    onClose: () => void;
    // D2 — cuando se llega aqui desde "Convertir en cliente", la persona de
    // prueba viaja hasta el formulario de inscripcion para rellenarlo y, al
    // guardar, enlazarla con su nueva ficha de cliente.
    prospect?: { id: string; name: string; phone: string; email: string };
}

interface DiaConNivel {
    dia: string;
    nivelId: string;
}

interface DisponibilidadResultado {
    dia: string;
    horaInicio: string;
    nivel: {
        _id: string;
        nombre: string;
    };
    ocupadas: number;
    disponibles: number;
    plazasActuales: Array<any>; // Puedes definir una interfaz más específica si lo necesitas
}

interface FilterState {
    frecuenciaSemanal: number | null; 
    mismoNivel: boolean | null;
    nivelUnico: string;
    dias: string[];           
    niveles: {               
        [key: string]: string; 
    };
    horarios: {             
        [key: string]: string; 
    };
    selecciones: Array<{
        dia: string;
        nivel_id: string;
        horaInicio: string;
    }>;
}

const UNLIMITED_WEEKLY_SESSIONS = -1;

const formatFrequencyLabel = (value: number) => (
    value === UNLIMITED_WEEKLY_SESSIONS ? 'Ilimitada' : `${value}x Semana`
);


// Añadir esta interfaz para los horarios disponibles
interface HorarioNivel {
    dia: string;
    hora: string;
    nivel: string;
    frecuencia: number;
    // Nuevos campos de disponibilidad
    ocupadas?: number;
    disponibles?: number;
    estado?: 'disponible' | 'completo';
}

interface HorariosPorDia {
    dia: string;
    nivel_id: string;
    horarios: HorarioNivel[];
}

const FiltroPlazas: React.FC<ModalProps> = ({ onClose, prospect }) => {
    const navigate = useNavigate();
    const { tenantCode } = useParams<{ tenantCode: string }>();
    
    const { data: niveles, isLoading: isLoadingNiveles } = useNivelesQuery();
    const { data: configuraciones } = useConfigurationsQuery();
    const frecuenciasDisponibles = useMemo(() => {
        const config = configuraciones?.[0];
        if (!config?.tarifas) return [1, 2, 3, 4, 5, 6];

        const frecuencias = config.tarifas.reduce((acc: number[], t: any) => {
            const value = typeof t.weeklySessions === 'number'
                ? t.weeklySessions
                : (typeof t.sesionesSemanales === 'number' ? t.sesionesSemanales : null);

            if (
                value !== null
                && Number.isInteger(value)
                && (value > 0 || value === UNLIMITED_WEEKLY_SESSIONS)
            ) {
                acc.push(value);
            }

            return acc;
        }, []);

        // Con tarifas por nivel se ofrecen también las frecuencias que tengan
        // precio en alguna de ellas. A propósito NO se ofrecen totales que solo
        // salen mezclando (2 suelo + 2 máquina = 4x): complicaba la búsqueda y
        // nadie lo pide. Si un centro quiere 4x, pone ese precio en una tarifa.
        (config.tarifasNombradas || []).forEach((t) => {
            t.precios.forEach((p) => {
                if (Number.isInteger(p.weeklySessions) && p.weeklySessions > 0) frecuencias.push(p.weeklySessions);
            });
        });

        return [...new Set(frecuencias)].sort((a, b) => {
            if (a === UNLIMITED_WEEKLY_SESSIONS) return 1;
            if (b === UNLIMITED_WEEKLY_SESSIONS) return -1;
            return a - b;
        });
    }, [configuraciones]);

    const resultadosRef = useRef<HTMLDivElement>(null);
    

    const [filtros, setFiltros] = useState<FilterState>({
        frecuenciaSemanal: null,
        mismoNivel: null,
        nivelUnico: '',
        dias: [],              // Añadido
        niveles: {},          // Añadido
        horarios: {},         // Añadido
        selecciones: []
    });


    // Variable que se usa para mostrar el modulo de plazas disponibles
    const [resultadosDisponibilidad, setResultadosDisponibilidad] = useState<DisponibilidadResultado[]>([]);

    // Variable que permite habilitar o no el botón de búsqueda y limpiado de busqueda
    const [isBuscando, setIsBuscando] = useState(false);


    // Nuevo estado para los horarios disponibles
    const [horariosDisponibles, setHorariosDisponibles] = useState<HorarioNivel[]>([]);

    // Estado de horarios por dia cuando se seleccionan Niveles Diferentes
    const [horariosPorDia, setHorariosPorDia] = useState<HorariosPorDia[]>([]);

    //mensaje a mostrar cuando filtremos 
    const [showAgostoMessage, setShowAgostoMessage] = useState(false);

    const isUnlimitedFrequency = filtros.frecuenciaSemanal === UNLIMITED_WEEKLY_SESSIONS;

    const diasSemana = [
        'Lunes',
        'Martes',
        'Miércoles',
        'Jueves',
        'Viernes'
    ];

    // Variable para la función de verificación de disponibilidad que recibe selecciones y verifica si ya existen
    // plazas registradas con los mismos datos de dia y hora y nivel
    const verificarDisponibilidad = useVerificarDisponibilidadPlazas();



    // Cuando se selecciona la frecuencia, resetear los días
    const handleFrecuenciaChange = (frecuencia: number) => {
        setFiltros(prev => ({
            ...prev,
            frecuenciaSemanal: prev.frecuenciaSemanal === frecuencia ? null : frecuencia,
            mismoNivel: (frecuencia === 1 || frecuencia === UNLIMITED_WEEKLY_SESSIONS) ? true : null,
            nivelUnico: '',
            dias: [],
            niveles: {},
            horarios: {},
            selecciones: []
        }));

         // Limpiar todos los estados relacionados
        setHorariosDisponibles([]);
        setHorariosPorDia([]);
        setResultadosDisponibilidad([]);
        setIsBuscando(false);
    };
    

    const handleMismoNivelChange = (mismo: boolean) => {
        setFiltros(prev => ({
            ...prev,
            mismoNivel: mismo,
            nivelUnico: '',
            dias: [],
            niveles: {},
            horarios: {},
            selecciones: []
        }));
        
        // Limpiar todos los estados relacionados
        setHorariosDisponibles([]);
        setHorariosPorDia([]);
        setResultadosDisponibilidad([]);
        setIsBuscando(false);
    };

    const handleNivelUnicoChange = (nivelId: string) => {
        setFiltros(prev => ({
            ...prev,
            nivelUnico: nivelId,
            selecciones: []
        }));

        // No necesitamos llamar obtenerHorariosNivel manualmente
        // El hook useDisponibilidadNivelQuery se encargará automáticamente
    };

    // NUEVO: Hook para obtener disponibilidad con colores
    const { 
        data: disponibilidadNivel, 
        isLoading: isLoadingDisponibilidad,
        showAgostoMessage: showAgostoFromHook 
    } = useDisponibilidadNivelQuery(
        filtros.mismoNivel === true ? filtros.nivelUnico : null
    );

    // Actualizar mensaje de agosto desde el hook
    useEffect(() => {
        setShowAgostoMessage(showAgostoFromHook);
    }, [showAgostoFromHook]);



    // Función para obtener los próximos eventos del nivel seleccionado
    const obtenerHorariosNivel = async (nivelId: string) => {
        try {
            
            //para testear funcionalidad de agosto
            // const fechaInicio = new Date(2025, 6, 20); // Mes 6 es julio (0-based)
            // const fechaFin = new Date(2025, 6, 20);
            const fechaInicio = new Date(); // comentar para probar funcionalidad de agosto
            const fechaFin = new Date(); // comentar para probar funcionalidad de agosto
            fechaFin.setDate(fechaFin.getDate() + 30);

            // Verificar si alguna de las fechas cae en agosto
            const mesInicio = fechaInicio.getMonth();
            const mesFin = fechaFin.getMonth();

            
            if (mesInicio === 7 || mesFin === 7) { // Si alguna fecha cae en agosto
                setShowAgostoMessage(true);
                // Establecer las fechas para septiembre
                fechaInicio.setMonth(8); // Septiembre
                fechaInicio.setDate(1); // Primer día del mes
                fechaFin.setMonth(8);
                fechaFin.setDate(14); // 14 días desde el inicio de septiembre
            } else {
                setShowAgostoMessage(false);
            }
    
            const eventos = await fetchEventosByDateRangeAndNivel(
                fechaInicio.toISOString(),
                fechaFin.toISOString(),
                nivelId
            );
    

            // console.log('Eventos:', eventos);

            // Convertir los eventos a EventoConDia
            const eventosConDia = eventos.map(evento => {
                const fecha = new Date(evento.fecha);
                const dia = diasSemana[fecha.getDay() - 1]; // -1 porque getDay() devuelve 1-7 y nuestro array es 0-4
                return {
                    ...evento,
                    dia
                } as EventoConDia;
            });
    
            // Procesamos los eventos para obtener los horarios únicos
            const horarios = eventosConDia.reduce((acc: HorarioNivel[], evento) => {
                const existe = acc.some(h => 
                    h.dia === evento.dia && 
                    h.hora === evento.horaInicio && 
                    h.nivel === evento.nivel_id
                );
    
                if (!existe) {
                    acc.push({
                        dia: evento.dia,
                        hora: evento.horaInicio,
                        nivel: evento.nivel_id,
                        frecuencia: 1
                    });
                }
    
                return acc;
            }, []);
    
            setHorariosDisponibles(horarios);
        } catch (error) {
            console.error('Error al obtener horarios:', error);
            toast.error('Error al obtener los horarios disponibles');
        }
    };

    // Funcion para obtener los horarios de las proximas 2 semanas filtrados por nivel y dia
    const obtenerHorariosNivelDia = async (dia: string, nivelId: string) => {
        try {
            // Ventana rodante: próximos 30 días desde hoy (sin salto a septiembre).
            const fechaInicio = new Date();
            const fechaFin = new Date();
            fechaFin.setDate(fechaFin.getDate() + 30);

            // Obtener disponibilidad del backend (con ocupadas/disponibles)
            const disponibilidadResponse = await fetchDisponibilidadNivel(
                nivelId,
                fechaInicio.toISOString(),
                fechaFin.toISOString()
            );

            // Filtrar solo los horarios del día específico
            const horariosDelDia = disponibilidadResponse.horarios
                .filter((horario: any) => horario.dia === dia)
                .map((horario: any) => ({
                    dia: horario.dia,
                    hora: horario.horaInicio,
                    nivel: horario.nivel_id,
                    frecuencia: 1,
                    // Agregar datos de disponibilidad
                    ocupadas: horario.ocupadas,
                    disponibles: horario.disponibles,
                    estado: horario.estado
                }));
    
            // Actualizar el estado con los nuevos horarios para este día
            setHorariosPorDia(prev => {
                const index = prev.findIndex(h => h.dia === dia);
                if (index >= 0) {
                    return prev.map(h => 
                        h.dia === dia 
                            ? { ...h, horarios: horariosDelDia }
                            : h
                    );
                }
                return [...prev, { dia, nivel_id: nivelId, horarios: horariosDelDia }];
            });
    
        } catch (error) {
            console.error('Error al obtener horarios:', error);
            toast.error('Error al obtener los horarios disponibles');
        }
    };


    // Verifica disponibilidad de plazas usando un endpoint de la API
    const handleBuscarPlazas = async () => {
        setIsBuscando(true);
        try {
            const selecciones = filtros.selecciones;
            const isUnlimited = filtros.frecuenciaSemanal === UNLIMITED_WEEKLY_SESSIONS;
            const hasRequiredSelections = isUnlimited
                ? selecciones.length > 0
                : selecciones.length === (filtros.frecuenciaSemanal ?? 0);

            if (!selecciones.length || !hasRequiredSelections) {
                return; // Validación básica
            }
            console.log('Buscando disponibilidad para selecciones:', selecciones);
            const disponibilidad = await verificarDisponibilidad.mutateAsync(selecciones);
            console.log('Respuesta de disponibilidad recibida:', disponibilidad);
            
            // Verificar si faltan resultados
            if (disponibilidad.length !== selecciones.length) {
                const resultadosCompletos = selecciones.map(seleccion => {
                    const resultadoExistente = disponibilidad.find(
                        d => d.dia === seleccion.dia && 
                            d.horaInicio === seleccion.horaInicio && 
                            d.nivel._id === seleccion.nivel_id
                    );
    
                    if (resultadoExistente) {
                        return resultadoExistente;
                    }
    
                    return {
                        dia: seleccion.dia,
                        horaInicio: seleccion.horaInicio,
                        nivel: {
                            _id: seleccion.nivel_id,
                            nombre: niveles?.find(n => n._id === seleccion.nivel_id)?.nombre || ''
                        },
                        ocupadas: 0,
                        disponibles: 0,
                        plazasActuales: []
                    };
                });
    
                setResultadosDisponibilidad(resultadosCompletos);
                // Se añade un scroll después de un pequeño delay para asegurar que los resultados se han renderizado
                setTimeout(() => {
                    resultadosRef.current?.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                    });
                }, 100);
            } else {
                setResultadosDisponibilidad(disponibilidad);
            }

            console.log('Disponibilidad:', disponibilidad);
    
        } catch (error) {
            console.error('Error al verificar disponibilidad:', error);
            toast.error('Error al verificar la disponibilidad');
        }
    };

    const handleSeleccionMultipleNiveles = async (selecciones: DiaConNivel[]) => {
        // console.log('Selecciones:', selecciones);
        setFiltros(prev => ({
            ...prev,
            selecciones: selecciones.map(s => ({
                dia: s.dia,
                nivel_id: s.nivelId,
                horaInicio: ''
            }))
        }));

        // Limpiar los horarios existentes antes de obtener los nuevos
        setHorariosPorDia([]);

        try {
            // Usar Promise.all para procesar todas las solicitudes en paralelo
            await Promise.all(
                selecciones.map(seleccion => 
                    obtenerHorariosNivelDia(seleccion.dia, seleccion.nivelId)
                )
            );
        } catch (error) {
            console.error('Error al obtener horarios:', error);
            toast.error('Error al obtener los horarios de los niveles seleccionados');
        }
    };


    const handleInscribirCliente = () => {  
        const plazasDisponibles = resultadosDisponibilidad.map((plaza) => {
            return {
                dia: plaza.dia,
                horaInicio: plaza.horaInicio,
                nivel_id: plaza.nivel._id,
                nivel_nombre: plaza.nivel.nombre
            };
        });

        // Navegar a la nueva página con los datos
        navigate(`/${tenantCode}/inscripcion-cliente`, {
            state: {
                plazasDisponibles,
                frecuenciaSemanal: filtros.frecuenciaSemanal,
                prospect
            }
        });
        // console.log('Plazas disponibles:', plazasDisponibles);
    };

    // Función para limpiar la búsqueda
    const handleLimpiarBusqueda = () => {
        setFiltros({
            frecuenciaSemanal: null,
            mismoNivel: null,
            nivelUnico: '',
            dias: [],
            niveles: {},
            horarios: {},
            selecciones: []
        });
        setHorariosDisponibles([]);
        setResultadosDisponibilidad([]);
        setHorariosPorDia([]);
        setIsBuscando(false);
    };

    // console.log('resultadosDisponibilidad',resultadosDisponibilidad);


    return (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative modal-content bg-white rounded-lg p-6 w-full max-w-4xl overflow-y-auto max-h-[80vh]">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <div className="flex flex-col justify-between items-left mb-4">
                    <h2 className="text-2xl font-bold">Filtro de Plazas</h2>
                    <div className='p-2'>
                        <p>En este apartado podrás crear un nuevo cliente.</p>
                        <p>Primero debes hacer una búsqueda de plazas libres y reservar la(s) plaza(s) del nuevo cliente.</p>
                        <p>Si encuentras plaza(s) libre(s) la(s) puedes reservar y crear el cliente nuevo.</p>
                    </div>

                    {/* Sección de filtros */}
                    <div className="mt-4 space-y-6">
                        {/* Frecuencia Semanal */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                ¿Cuántas veces por semana quiere entrenar el cliente?
                            </label>
                            <div className="flex gap-2">
                                {frecuenciasDisponibles.map(freq => (
                                    <button
                                        key={freq}
                                        onClick={() => handleFrecuenciaChange(freq)}
                                        className={`px-4 py-2 rounded-md ${
                                            filtros.frecuenciaSemanal === freq
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}
                                    >
                                        {formatFrequencyLabel(freq)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Paso 2: Mismo nivel o diferentes (solo para 2x o 3x) */}
                        {filtros.frecuenciaSemanal && filtros.frecuenciaSemanal > 1 && !isUnlimitedFrequency && (
                            <div className="space-y-2 mt-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    ¿Las clases serán del mismo nivel?
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleMismoNivelChange(true)}
                                        className={`px-4 py-2 rounded-md ${
                                            filtros.mismoNivel === true
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}
                                    >
                                        Mismo nivel
                                    </button>
                                    <button
                                        onClick={() => handleMismoNivelChange(false)}
                                        className={`px-4 py-2 rounded-md ${
                                            filtros.mismoNivel === false
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}
                                    >
                                        Niveles diferentes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Paso 3: Selección de nivel(es) */}
                        {filtros.mismoNivel !== null && (
                            <div className="mt-4">
                                {filtros.mismoNivel ? (
                                    // Selector único de nivel
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Selecciona el nivel para todas las clases
                                        </label>
                                        <select
                                            value={filtros.nivelUnico}
                                            onChange={(e) => handleNivelUnicoChange(e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                        >
                                            <option value="">Seleccionar nivel</option>
                                            {niveles?.map(nivel => (
                                                <option key={nivel._id} value={nivel._id}>
                                                    {nivel.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    // Configuración individual por día
                                    // Este componente lo implementaremos después
                                    <div>
                                        <ConfiguracionNiveles 
                                            niveles={niveles || []}
                                            diasSemana={diasSemana}
                                            frecuenciaSemanal={filtros.frecuenciaSemanal ?? 0}
                                            onSeleccionNivel={handleSeleccionMultipleNiveles}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        
                    </div>
                    {showAgostoMessage && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-700">
                                Nota: Durante el mes de agosto los horarios son diferentes. 
                                Mostrando los horarios que estarán disponibles a partir de septiembre.
                            </p>
                        </div>
                    )}

                    {/* Preview de Horarios Disponibles con COLORES */}
                    {disponibilidadNivel && Array.isArray(disponibilidadNivel) && disponibilidadNivel.length > 0 && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <h3 className="text-lg font-semibold mb-2">
                                Selecciona los horarios ({
                                    isUnlimitedFrequency
                                        ? `${filtros.selecciones.length}/Ilimitada`
                                        : `${filtros.selecciones.length}/${filtros.frecuenciaSemanal ?? 0}`
                                })
                            </h3>
                            {isLoadingDisponibilidad && (
                                <div className="text-center py-4 text-gray-500">
                                    Cargando disponibilidad...
                                </div>
                            )}
                            <div className="grid grid-cols-5 gap-4">
                                {diasSemana.map(dia => (
                                    <div key={dia} className="space-y-2">
                                        <h4 className="font-medium text-center">{dia}</h4>
                                        {disponibilidadNivel
                                            .filter((h: any) => h.dia === dia)
                                            .sort((a: any, b: any) => {
                                                const horaA = parseInt(a.horaInicio.split(':')[0]);
                                                const horaB = parseInt(b.horaInicio.split(':')[0]);
                                                return horaA - horaB;
                                            })
                                            .map((horario: any, idx: number) => {
                                                const isSelected = filtros.selecciones.some(
                                                    s => s.dia === dia && s.horaInicio === horario.horaInicio
                                                );
                                                const isCompleto = horario.estado === 'completo';
                                                
                                                return (
                                                    <button
                                                        key={idx}
                                                        onClick={() => {
                                                            if (isCompleto) return; // No permitir selección si está completo
                                                            
                                                            if (isSelected) {
                                                                setFiltros(prev => ({
                                                                    ...prev,
                                                                    selecciones: prev.selecciones.filter(
                                                                        s => !(s.dia === dia && s.horaInicio === horario.horaInicio)
                                                                    )
                                                                }));
                                                            } else if (
                                                                filtros.frecuenciaSemanal !== null
                                                                && (isUnlimitedFrequency || filtros.selecciones.length < filtros.frecuenciaSemanal)
                                                            ) {
                                                                setFiltros(prev => ({
                                                                    ...prev,
                                                                    selecciones: [
                                                                        ...prev.selecciones,
                                                                        {
                                                                            dia,
                                                                            nivel_id: filtros.nivelUnico,
                                                                            horaInicio: horario.horaInicio
                                                                        }
                                                                    ]
                                                                }));
                                                            }
                                                        }}
                                                        className={`w-full text-sm p-2 rounded border transition-all ${
                                                            isSelected
                                                                ? 'bg-blue-500 text-white border-blue-600 ring-2 ring-blue-300'
                                                                : isCompleto
                                                                ? 'bg-red-500 text-white border-red-600 cursor-not-allowed opacity-70'
                                                                : 'bg-green-500 text-white border-green-600 hover:bg-green-600'
                                                        }`}
                                                        disabled={
                                                            isCompleto
                                                            || (
                                                                !isSelected
                                                                && filtros.frecuenciaSemanal !== null
                                                                && !isUnlimitedFrequency
                                                                && filtros.selecciones.length >= filtros.frecuenciaSemanal
                                                            )
                                                        }
                                                        title={
                                                            isCompleto 
                                                                ? 'Plaza completa - No disponible' 
                                                                : `${horario.disponibles} plaza${horario.disponibles !== 1 ? 's' : ''} disponible${horario.disponibles !== 1 ? 's' : ''}`
                                                        }
                                                    >
                                                        <div className="font-medium">{horario.horaInicio}</div>
                                                        <div className="text-xs mt-1">
Si                                                            {isCompleto ? 'Completo' : `${horario.ocupadas ?? 0}/${(horario.ocupadas ?? 0) + (horario.disponibles ?? 0)} ocupadas`}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                        
                    {/* Preview de Horarios Disponibles por día y nivel */}
                    {horariosPorDia.length > 0 && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <h3 className="text-lg font-semibold mb-2">
                            Selecciona los horarios ({
                                // Contar solo las selecciones que tienen horario
                                isUnlimitedFrequency
                                    ? `${filtros.selecciones.filter(s => s.horaInicio !== '').length}/Ilimitada`
                                    : `${filtros.selecciones.filter(s => s.horaInicio !== '').length}/${filtros.frecuenciaSemanal ?? 0}`
                            })
                            </h3>
                            <div className="flex gap-4">
                                {horariosPorDia.sort((a, b) => {
                                    // Usar el índice del día en diasSemana para ordenar
                                    const indexA = diasSemana.indexOf(a.dia);
                                    const indexB = diasSemana.indexOf(b.dia);
                                    return indexA - indexB;
                                }).map(({ dia, nivel_id, horarios }) => (
                                    <div key={dia} className="flex-1 p-4 border rounded-lg">
                                        <h4 className="font-medium mb-2">
                                            {dia} - {niveles?.find(n => n._id === nivel_id)?.nombre}
                                        </h4>
                                        <div className="space-y-2">
                                            {horarios
                                                .sort((a, b) => {
                                                    const horaA = parseInt(a.hora.split(':')[0]);
                                                    const horaB = parseInt(b.hora.split(':')[0]);
                                                    return horaA - horaB;
                                                })
                                                .map((horario, idx) => {
                                                    const isSelected = filtros.selecciones.some(
                                                        s => s.dia === dia && s.horaInicio === horario.hora
                                                    );
                                                    const isCompleto = horario.estado === 'completo';
                                                    
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => {
                                                                if (isCompleto) return; // No permitir selección si está completo
                                                                
                                                                setFiltros(prev => ({
                                                                    ...prev,
                                                                    selecciones: prev.selecciones.map(s =>
                                                                        s.dia === dia
                                                                            ? { ...s, horaInicio: isSelected ? '' : horario.hora }
                                                                            : s
                                                                    )
                                                                }));
                                                            }}
                                                            className={`w-full text-sm p-2 rounded border transition-all ${
                                                                isSelected
                                                                    ? 'bg-blue-500 text-white border-blue-600 ring-2 ring-blue-300'
                                                                    : isCompleto
                                                                    ? 'bg-red-500 text-white border-red-600 cursor-not-allowed opacity-70'
                                                                    : 'bg-green-500 text-white border-green-600 hover:bg-green-600'
                                                            }`}
                                                            disabled={isCompleto}
                                                            title={
                                                                isCompleto 
                                                                    ? 'Plaza completa - No disponible' 
                                                                    : horario.disponibles !== undefined 
                                                                        ? `${horario.disponibles} plaza${horario.disponibles !== 1 ? 's' : ''} disponible${horario.disponibles !== 1 ? 's' : ''}`
                                                                        : horario.hora
                                                            }
                                                        >
                                                            <div className="font-medium">{horario.hora}</div>
                                                            <div className="text-xs mt-1">
                                                                {isCompleto 
                                                                    ? 'Completo' 
                                                                    : horario.disponibles !== undefined 
                                                                        ? `${horario.ocupadas ?? 0}/${(horario.ocupadas ?? 0) + (horario.disponibles ?? 0)} ocupadas`
                                                                        : ''}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Botón de búsqueda y limpiar búsqueda */}
                    <div className="flex gap-4 mt-10">
                        <button
                            onClick={handleBuscarPlazas}
                            disabled={
                                // isBuscando ||
                                filtros.frecuenciaSemanal === null || 
                                filtros.selecciones.length === 0 ||
                                (!isUnlimitedFrequency && filtros.selecciones.length !== (filtros.frecuenciaSemanal ?? 0)) ||
                                (filtros.mismoNivel === true && !filtros.nivelUnico) ||
                                filtros.selecciones.some(s => !s.horaInicio)
                            }
                            className="flex-1 bg-blue-500 text-white py-2 rounded-md disabled:bg-gray-300"
                        >
                            {/* {isBuscando ? 'Buscando...' : 'Buscar Plazas Disponibles'} */}
                            Buscar Plazas Disponibles
                        </button>

                        {(isBuscando || resultadosDisponibilidad.length > 0) && (
                            <button
                                onClick={handleLimpiarBusqueda}
                                className="flex-1 bg-gray-500 text-white py-2 rounded-md hover:bg-gray-600"
                            >
                                Limpiar búsqueda
                            </button>
                        )}
                    </div>
                    <div>
                    {resultadosDisponibilidad.length > 0 && (
                        <div 
                            className="mt-6 p-4 bg-white rounded-lg shadow"
                            ref={resultadosRef}
                        >
                            <h3 className="text-lg font-semibold mb-4">Resultados de disponibilidad</h3>
                            <div className="space-y-4">
                                {resultadosDisponibilidad.map((resultado, index) => (
                                    <div 
                                        key={index}
                                        className={`p-4 rounded-lg border ${
                                            resultado.disponibles > 0 
                                                ? 'border-green-200 bg-green-50' 
                                                : 'border-red-200 bg-red-50'
                                        }`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="font-medium">
                                                    {resultado.dia} - {resultado.horaInicio}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    Nivel: {resultado.nivel.nombre}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-bold ${
                                                    resultado.disponibles > 0 
                                                        ? 'text-green-600' 
                                                        : 'text-red-600'
                                                }`}>
                                                    {resultado.disponibles} plaza{resultado.disponibles !== 1 ? 's' : ''} disponible{resultado.disponibles !== 1 ? 's' : ''}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    ({resultado.ocupadas} ocupadas de {resultado.ocupadas + resultado.disponibles})
                                                </p>
                                            </div>
                                        </div>
                                        {resultado.plazasActuales.length > 0 && (
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500 mb-1">
                                                    Alumnos actuales:
                                                </p>
                                                <div className="text-sm text-gray-600 pl-4">
                                                    {resultado.plazasActuales.map((plaza, idx) => (
                                                        <p key={idx}>• {plaza.clienteNombre || 'Cliente sin nombre'}</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                                <div className='mt-4 text-right'>
                                    <button
                                        onClick={handleInscribirCliente}
                                        disabled={!resultadosDisponibilidad.every(r => r.disponibles > 0)}
                                        title={(!resultadosDisponibilidad.every(r => r.disponibles > 0)) ? "No hay plazas disponibles en todos los horarios seleccionados": "Inscribir Cliente Nuevo"}
                                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    >
                                        Inscribir Cliente
                                    </button>
                                </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FiltroPlazas;
